/**
 * Les astuces de l'écran de chargement : des gestes qui existent
 * mais ne se voient pas dans l'interface. Télégraphiques, à lire d'un coup
 * d'œil — geste → effet. Chacune doit rester vraie : elles recopient le
 * manuel (`convex/systemSkills/manual-shortcuts` et voisins), à tenir à jour
 * avec lui.
 *
 * Balisage, rendu en pastilles :
 * - `[Shift]` une touche. `[Mod]` et `[Alt]` prennent le libellé de la
 *   plateforme (⌘ / ⌥ sur Mac) ;
 * - `{click}` un geste de souris, parmi `GESTURES`, rendu avec son icône.
 *
 * Le texte vit dans `shell:loading.tips.<clé>` (même balisage dans chaque
 * langue) : on ne garde ici que les clés, traduites au render par
 * `LoadingScreen`.
 */
const LOADING_TIPS = [
  "altClickBlock",
  "altClickCanvas",
  "shiftDrag",
  "modDrag",
  "appearance",
  "compactFrame",
  "mention",
  "scaleTitle",
  "lasso",
  "alignSelection",
  "dragConnection",
  "dropBlock",
  "search",
  "jump",
  "bookmark",
  "frame",
  "pan",
  "editLabel",
  "snapWindow",
  "dictate",
] as const;

/** Clé d'une astuce, dans `shell:loading.tips`. */
export type LoadingTipKey = (typeof LOADING_TIPS)[number];

const IS_MAC =
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad/i.test(navigator.platform);

const KEY_LABELS: Record<string, string> = IS_MAC
  ? { Mod: "⌘", Alt: "⌥" }
  : { Mod: "Ctrl" };

export const GESTURES = [
  "click",
  "right-click",
  "double-click",
  "drag",
] as const;
export type Gesture = (typeof GESTURES)[number];

export type TipPart =
  | { kind: "text"; text: string }
  | { kind: "key"; text: string }
  | { kind: "gesture"; gesture: Gesture };

const isGesture = (value: string): value is Gesture =>
  (GESTURES as readonly string[]).includes(value);

/** Découpe une astuce en texte, touches et gestes. */
export function parseTip(tip: string): TipPart[] {
  return tip
    .split(/(\[[^\]]+\]|\{[^}]+\})/)
    .filter((chunk) => chunk !== "")
    .map((chunk): TipPart => {
      const inner = chunk.slice(1, -1);
      if (chunk.startsWith("[")) {
        return { kind: "key", text: KEY_LABELS[inner] ?? inner };
      }
      if (chunk.startsWith("{") && isGesture(inner)) {
        return { kind: "gesture", gesture: inner };
      }
      return { kind: "text", text: chunk };
    });
}

let lastTipIndex = -1;

/** Une astuce au hasard (sa clé), jamais la même deux chargements de suite. */
export function pickLoadingTip(): LoadingTipKey {
  let index = Math.floor(Math.random() * LOADING_TIPS.length);
  if (index === lastTipIndex) index = (index + 1) % LOADING_TIPS.length;
  lastTipIndex = index;
  return LOADING_TIPS[index];
}
